import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Typography, message, Tabs, Table, Pagination, Flex, Select, Tooltip } from "antd";
import { useStore } from "../../stores/root-store-context";
import { observer } from "mobx-react-lite";
import Layout from "../../components/layout/Layout";
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { TariffPlans } from "../../stores/TariffPlansStore";
import Tariffs from "../../components/sections/Tariffs";
import { FileTextOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const PaymentAndTariffs = observer(() => {
  const { tariffPlansStore, paymentStore, applicationStore, countryPaymentSystemStore, countryStore, currencyExchangeRateStore } = useStore();

  // TODO: Change to dynamic definition of currency
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();

  // TODO: If there is a discount system, change this logic
  const discount = applicationStore.isUaCompany ? 0.75 : 0;

  const pageSize = 4;
  const currentTariffPlan = tariffPlansStore.tariffPlans.find(
    (plan) => plan.id === applicationStore.company?.TariffPlanId
  );

  useEffect(() => {
    const checkCompany = async () => {
      await applicationStore.checkIsUACompany();
    };
    checkCompany();
  }, []);

  useEffect(() => {
    const getLocalPricing = async () => {
      await currencyExchangeRateStore.getLatestCurrencyExchangeRate();
      await tariffPlansStore.recalcLocalPricing();
    };
    getLocalPricing();
  }, [currencyExchangeRateStore.currentCurrency]);

  // TODO: Change to dynamic definition of currency
  useEffect(() => {
    const target = applicationStore.isUaCompany ? "UAH" : "USD";
    if (currencyExchangeRateStore.currentCurrency !== target) {
      currencyExchangeRateStore.setCurrentCurrency(target);
    }
  }, [applicationStore.isUaCompany, currencyExchangeRateStore.currentCurrency]);

  const { Option } = Select;

  useEffect(() => {
    tariffPlansStore.getTariffPlans();

    if (applicationStore.company?.Id) {
      paymentStore.getBalance(applicationStore.company.Id);
      paymentStore.getPaymentDate(applicationStore.company.Id);
      paymentStore.getTransactionHistory(applicationStore.company.Id);
    }
  }, [tariffPlansStore, applicationStore, paymentStore]);

  useEffect(() => {
    if (applicationStore.company?.CountryId) {
      countryPaymentSystemStore.GetAllAvailablePaymentSystems(applicationStore.company.CountryId);
    }

  }, [applicationStore.company?.Id, countryPaymentSystemStore]);

  const handleSelectPlan = async (plan: TariffPlans, payment: number, interval: "Monthly" | "Yearly") => {
    const currentTariffPlanId = applicationStore.company?.TariffPlanId;

    if (currentTariffPlanId === plan.id) {
      message.info(t("topUpBalanceInfo"));
      await initiatePayment(plan, payment, interval);
    } else {
      const paymentDate = paymentStore.paymentDate;
      const currentDate = dayjs();

      if (paymentDate.isAfter(currentDate)) {
        message.warning(t("switchTariffWarning"));
      } else {
        try {
          const changeTariffResponse = await paymentStore.changeTariffPlan(applicationStore.company?.Id || "", plan.id, interval);
          if (!changeTariffResponse.isSuccess) {
            message.error(t("changeTariffError"));
            return;
          }
        } catch (error) {
          console.error(t("changeTariffError"), error);
          message.error(t("changeTariffError"));
          return;
        }
        message.info(t("tariffChanged"));
        await initiatePayment(plan, payment, interval);
      }
    }
  };

  const applyDiscount = (amount: number, discount: number) => {
    const d = Math.max(0, Math.min(1, discount || 0));
    return Number((amount * (1 - d)).toFixed(2));
  };

  const initiatePayment = async (plan: TariffPlans, payment: number, interval: "Monthly" | "Yearly") => {
    try {
      const amount = applyDiscount(payment, discount);

      const formData = {
        amount: amount,
        currency: "USD",
        description: `${plan.name} ${interval} Plan`,
        order_id: `order_${Date.now()}`,
      };

      const res = await paymentStore.createPaymentRequest(applicationStore.company?.Id || "", formData);

      if (res?.type === "html" && res.payload) {
        const paymentContainer = document.createElement("div");
        paymentContainer.innerHTML = res.payload;
        document.body.appendChild(paymentContainer);
        const form = paymentContainer.querySelector("form") as HTMLFormElement | null;
        if (form) form.submit();
        else message.error(t("ErrorGeneratingPaymentForm"));
      } else if (res?.type === "url" && res.payload) {
        window.location.href = res.payload; // Stripe Checkout
      } else {
        message.error(t("ErrorInitiatingPayment"));
      }
    } catch (error) {
      console.error("Payment error:", error);
      message.error(t("ErrorInitiatingPayment"));
    }
  };

  const isValidUrl = (value?: string) => {
    if (!value) return false;
    try {
      const u = new URL(value);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  const transactionColumns = [
    {
      title: t('date'),
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      align: 'left' as 'left',
      render: (text: string) => {
        let formattedDate;
        try {
          const cleanedDate = text.split('.')[0] + 'Z';
          const localDate = new Date(cleanedDate);

          formattedDate = localDate.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).replace(/\//g, '.').replace(',', '');
        } catch (error) {
          console.error('Error formatting transaction date:', error);
          formattedDate = 'Invalid Date';
        }

        return formattedDate;
      },
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'left' as 'left',
      render: (text: number) => (
        <span style={{ color: text < 0 ? 'red' : 'green' }}>
          $ {text}
        </span>
      ),
    },
    ...(applicationStore.isUaCompany
      ? [{
        title: t('localizedAmount'),
        dataIndex: 'amount',
        key: 'amountLocal',
        align: 'left' as 'left',
        render: (usd: number) => (
          <span style={{ color: usd < 0 ? 'red' : 'green' }}>
            ₴ {(usd * currencyExchangeRateStore.unitsPerUsd).toFixed(2)}
          </span>
        ),
      }] as const
      : []),
    {
      title: t('status'),
      dataIndex: 'transactionStatus',
      key: 'transactionStatus',
      align: 'left' as 'left',
    },
    {
      title: t('checkDetail'),
      dataIndex: 'payDetailId',
      key: 'payDetailId',
      width: 120,
      align: 'center' as const,
      render: (link: string) => {
        const valid = isValidUrl(link);
        if (!valid) {
          return (
            <Tooltip title={t('NoReceiptAvailable')}>
              <Button type="text" icon={<FileTextOutlined style={{ fontSize: 18 }} />} disabled />
            </Tooltip>
          );
        }

        return (
          <Tooltip title={t('OpenReceipt')}>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('OpenReceipt')}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <FileTextOutlined style={{ fontSize: 18 }} />
            </a>
          </Tooltip>
        );
      },
    },
  ];

  const paginatedData = paymentStore.transactionHistory
    .slice()
    .sort((a, b) => dayjs(b.transactionDate).diff(dayjs(a.transactionDate)))
    .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <Layout headerStyle={1} footerStyle={2}>
      <div style={{ margin: '50px auto', maxWidth: '1200px' }}>

        <Card
          bordered={false}
          style={{ marginBottom: '30px', padding: '20px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
        >
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab={t('BillingPayment')} key="1">
              <div style={{ textAlign: 'left', margin: '20px' }}>
                <Title level={4}>
                  {t('currentBalance')}:{' '}
                  <span style={{ color: '#1890ff' }}>
                    <span id="usdPrice">$ {paymentStore.balance.toFixed(2)}</span>
                    {applicationStore.isUaCompany && (
                      <>
                        <span> / </span>
                        <span id="localPrice">₴ {(paymentStore.balance * currencyExchangeRateStore.unitsPerUsd).toFixed(2)}</span>
                      </>
                    )}
                  </span>
                </Title>
                <Text>
                  {paymentStore.trialDate &&
                    paymentStore.trialDate.isSame(paymentStore.paymentDate, 'day') &&
                    dayjs().isBefore(paymentStore.trialDate) ? (
                    <>
                      {t('trialPeriodEnds')}: <span style={{ color: '#1890ff' }}>{paymentStore.trialDate.format('YYYY.MM.DD')}</span>
                    </>
                  ) : (
                    <>
                      {t('nextPaymentDate')}: <span style={{ color: '#1890ff' }}>{paymentStore.paymentDate.format('YYYY.MM.DD')}</span>
                    </>
                  )}
                </Text>


                <Text style={{ display: 'block', marginTop: '10px' }}>
                  {t('SubscriptionPlan')}:
                  <span style={{ color: '#1890ff' }}> {currentTariffPlan ? t(`Tariffs.${currentTariffPlan.name}.Title`) : ' '} {t(`${applicationStore.company.TariffPlanInterval}`)}</span>
                </Text>
                <div style={{ marginTop: '20px' }}>
                  <Flex gap={10} align="center">
                    <Select
                      style={{ width: 200 }}
                      placeholder={t("ChoosePaymentSystem")}
                      loading={countryPaymentSystemStore.isLoading}
                      value={applicationStore.company?.PaymentSystemId}
                      onChange={async (value) => {
                        if (!applicationStore.company) return;

                        try {
                          await applicationStore.editCompany({
                            ...applicationStore.company,
                            PaymentSystemId: value
                          });

                          await applicationStore.updateCompany();

                          message.success(t("PaymentSystemUpdated"));
                        } catch (err) {
                          console.error("Failed to update payment system", err);
                          message.error(t("ErrorUpdatingPaymentSystem"));
                        }
                      }}
                    >
                      {countryPaymentSystemStore.availableSystems.map((system) => (
                        <Option key={system.id} value={system.id}>
                          {system.name}
                        </Option>
                      ))}
                    </Select>

                    <Button onClick={async () => {
                      if (currentTariffPlan) {
                        let price = applicationStore.company.TariffPlanInterval === "Yearly" ? currentTariffPlan.pricePerYear : currentTariffPlan.pricePerMonth;
                        handleSelectPlan(
                          currentTariffPlan,
                          await price,
                          currentTariffPlan.description as any
                        );
                      }
                    }}>{t('topUp') + ' ' + t('Balance')}</Button>
                  </Flex>
                </div>
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={t('transactionHistory')} key="2">
              <Table
                columns={transactionColumns}
                dataSource={paginatedData}
                rowKey="id"
                pagination={false}
              />
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={paymentStore.transactionHistory.length}
                onChange={(page) => setCurrentPage(page)}
                style={{ marginTop: '20px', textAlign: 'center' }}
              />
            </Tabs.TabPane>
          </Tabs>
        </Card>
        <Title level={3} style={{ textAlign: 'center', marginBottom: '20px' }}>
          {t('PlanType')}
        </Title>
        <Tariffs
          currentTariff={currentTariffPlan?.id.toString()}
          currentIntervalType={applicationStore.company?.TariffPlanInterval || "Monthly" as any}
          discount={discount}
          callBackSaving={(tariffId, interval) => {
            var tariff = tariffPlansStore.tariffPlans.find(t => t.id === tariffId);
            if (!tariff) return;
            var payment = interval === "Yearly" ?
              tariff?.pricePerYear :
              tariff?.pricePerMonth

            handleSelectPlan(tariff, payment, interval)

          }} />
      </div>
    </Layout>
  );
});


export default PaymentAndTariffs;
